import { Background, BackgroundColor, Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import React, { useEffect, useRef, useState } from "react"
import { Repeater, Text, types, useAdminContext, usePageValues } from "react-bricks/frontend"

import { PDPProps } from "./PDP"
import classNames from "classnames"
import { twMerge } from "tailwind-merge"
import { useLocalesState } from "../../shared/context/locales"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"
import { useScrollSize } from "../../hooks/use-scroll-size"
import { useWindowSize } from "../../hooks/use-window-size-admin"

export interface TabbedPDPProps {
    repeater?: {
        id: string
        props: PDPProps
        type: "PDP"
    }[]
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    id?: string
}

const TabbedPDP: types.Brick<TabbedPDPProps> = ({
    repeater,
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
    id,
}) => {
    const { region } = useLocalesState()
    const { products: productsBuildTime, page: pageContext } = usePageContext()
    const client = useSDK()

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", ...(repeater?.map(r => r.props.sku).filter(r => r) || []), region, page.language],
        queryFn: ({ signal }) =>
            client.getProducts(
                { sku: repeater?.map(r => r.props.sku).filter((r): r is string => !!r), country: region },
                page.language,
                signal
            ),
        enabled: !!repeater?.length,
        meta: { background: true },
    })

    const products = data || productsBuildTime

    const [activeTab, setActiveTab] = useState(0)

    const ref = useRef<HTMLUListElement>(null)

    const changeTab = (id: string) => {
        if (!repeater) return
        setActiveTab(repeater.findIndex(r => r.id === id))
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    }

    const { width } = useWindowSize({ initializeWithValue: false, debounceDelay: 200 })

    const { width: tabWidth } = useScrollSize({ ref, initializeWithValue: false, debounceDelay: 200 })

    const { isAdmin } = useAdminContext()

    useEffect(() => {
        if (!products || !repeater) return

        setActiveTab(repeater.findIndex(r => !r.props.sku || products.map(p => p.sku).includes(r.props.sku)) || 0)
    }, [products, repeater])

    return (
        <Background color={BackgroundColor.White} className="overflow-hidden">
            <Container>
                <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
                <div className="flex flex-col" id={id}>
                    {repeater && repeater.length > 1 && (
                        <ul
                            ref={ref}
                            className={twMerge(
                                classNames(
                                    "hidden-scrollbar -mx-6.25 mb-7 flex flex-row gap-10 overflow-auto px-6.25 sm:mx-0 sm:mb-10 sm:justify-center md:gap-10 lg:mb-16 lg:gap-16",
                                    {
                                        "justify-center": !!width && !!tabWidth && width >= tabWidth,
                                    }
                                )
                            )}
                        >
                            {repeater.map((r, i) => {
                                if (r.props.sku && !products?.find(p => p.sku === r.props.sku) && !isAdmin) return null
                                return (
                                    <li
                                        key={r.id}
                                        id={r.id}
                                        className={twMerge(
                                            "relative mb-3.5 flex min-w-fit items-center justify-center text-center text-md font-semibold text-blue opacity-30 sm:text-lg lg:text-xl",
                                            activeTab === i &&
                                                "opacity-100 after:absolute after:-bottom-2.5 after:h-1 after:w-[calc(100%_+_8px)] after:rounded-[2px] after:bg-blue"
                                        )}
                                    >
                                        <Text
                                            propName={`title-${r.id}`}
                                            renderBlock={({ children }) => (
                                                <button className="h-full w-full" onClick={() => changeTab(r.id)}>
                                                    {children}
                                                </button>
                                            )}
                                            renderPlaceholder={({ children }) => (
                                                <button className="h-full w-full" onClick={() => changeTab(r.id)}>
                                                    {children}
                                                </button>
                                            )}
                                            placeholder="Lorem ipsum"
                                        />
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                    <Repeater
                        propName="repeater"
                        itemProps={
                            {
                                activeTab,
                                inline: true,
                                products,
                            } as PDPProps
                        }
                    />
                </div>
                <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
            </Container>
        </Background>
    )
}

TabbedPDP.schema = {
    name: "TabbedPDP",
    label: "Tabbed PDP",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/TabbedPDP.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "PDP",
                    label: "Product",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "Preselected SKU",
            type: types.SideEditPropType.Text,
        },
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): TabbedPDPProps => ({
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Small,
        variantBottom: SpacerVariant.Small,
    }),
}

export default TabbedPDP
